import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";

export interface MissingPackageRule {
  index: number;
  ruleId: string;
  ruleText: string;
  packageName: string;
}

interface PackagesNotInReplicateModalProps {
  open: boolean;
  onClose: () => void;
  missingRules: MissingPackageRule[];
  onEditRule: (rule: MissingPackageRule) => void;
  onDeleteRule: (rule: MissingPackageRule) => void;
  readOnlyViewer?: boolean;
}

const PackagesNotInReplicateModal: React.FC<PackagesNotInReplicateModalProps> = ({
  open,
  onClose,
  missingRules,
  onEditRule,
  onDeleteRule,
  readOnlyViewer = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "#1b1a19",
          color: "#ffffff",
          borderBottom: "1px solid #3b3a39",
        }}
      >
        <Typography variant="inherit" fontWeight="bold" color="#ffffff">
          Packages Not in Library
        </Typography>
        <Typography variant="caption" sx={{ color: "#A19F9D", display: "block", mt: 0.5 }}>
          {missingRules.length} rule{missingRules.length === 1 ? "" : "s"} reference packages that aren't installed for this customer.
          {readOnlyViewer ? "" : " Edit each rule to pick the package version, or delete the rule."}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19", padding: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {missingRules.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center", color: "#A19F9D" }}>
              All package issues resolved.
            </Box>
          )}
          {missingRules.map((rule) => (
            <Box
              key={rule.ruleId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1.25,
                borderBottom: "1px solid #3b3a39",
                "&:last-child": { borderBottom: "none" },
                "&:hover": { backgroundColor: "#252423" },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  <Box component="span" sx={{ color: "#a19f9d", mr: 0.5 }}>
                    {rule.index}:
                  </Box>
                  <Box component="span" sx={{ color: "#F3F2F1", fontWeight: 700 }}>
                    {rule.packageName}
                  </Box>
                  <Box component="span" sx={{ color: "#c8c6c4" }}>
                    {rule.ruleText.startsWith(rule.packageName) ? rule.ruleText.slice(rule.packageName.length) : ` ${rule.ruleText}`}
                  </Box>
                </Typography>
              </Box>
              {!readOnlyViewer && (
                <>
                  <Tooltip title="Edit rule to pick a package version" arrow>
                    <IconButton size="small" sx={{ color: "#8a8886" }} onClick={() => onEditRule(rule)}>
                      <Icon iconName="Edit" style={{ fontSize: "small" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete this rule" arrow>
                    <IconButton size="small" sx={{ color: "#d5878b" }} onClick={() => onDeleteRule(rule)}>
                      <Icon iconName="Delete" style={{ fontSize: "small" }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", padding: 2, borderTop: "1px solid #3b3a39" }}>
        <Button variant="outlined" onClick={onClose} sx={{ color: "#ffffff", borderColor: "#757575" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PackagesNotInReplicateModal;
